import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Spinner from '@/components/common/spinner/Spinner';
import AlertMessage from '@/components/layout/message/AlertMessage';
import { useVirtualSimulation } from './hooks/useVirtualSimulation';
import StartScreen from './components/screens/StartScreen';
import BreakScreen from './components/screens/BreakScreen';
import SimulationCompleted from './components/simulationCompleted/SimulationCompleted';
import ExamUI from './components/examUI/ExamUI';

const VirtualSimulationPresenter: React.FC = () => {
    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add("solid-public-bg");
        return () => document.body.classList.remove("solid-public-bg");
    }, []);

    const {
        state,
        currentSessionNumber,
        questions,
        answers,
        handleAnswerChange,
        currentQuestion,
        currentQuestionIndex,
        goToQuestion,
        goToNext,
        goToPrevious,
        handleStartSession,
        handleSubmitSession,
    } = useVirtualSimulation();

    const handleGoBackWithWarning = () => {
        if (window.confirm("El tiempo seguirá corriendo. ¿Estás seguro de que quieres salir y volver al panel?")) {
            navigate('/student-home');
        }
    };

    if (state.isLoading) {
        return <Spinner text="Cargando simulacro..." />;
    }

    if (state.error) {
        return (
            <div style={{ padding: '2rem' }}>
                <AlertMessage type="error" title="Error al cargar">
                    {state.error.generalMessage}
                </AlertMessage>
            </div>
        );
    }

    const getSessionDuration = (sessionNum: number) =>
        state.simulation?.sessions?.find(s => s.number === sessionNum)?.duration ?? 0;

    switch (state.status) {
        case 'not_started':
            return (
                <StartScreen
                    simulationName={state.simulation?.name || 'Simulacro'}
                    sessionNumber={1}
                    durationMinutes={getSessionDuration(1)}
                    onStart={handleStartSession}
                    isSubmitting={state.isSubmitting}
                />
            );

        case 'break':
            return (
                <BreakScreen
                    nextSessionNumber={2}
                    durationMinutes={getSessionDuration(2)}
                    onStartNextSession={handleStartSession}
                    isSubmitting={state.isSubmitting}
                />
            );

        case 'in_progress_s1':
        case 'in_progress_s2':
            const activeSessionTests = state.simulation?.sessions
                ?.find(s => s.number === currentSessionNumber)
                ?.tests || [];

            return (
                <ExamUI
                    simulationName={state.simulation?.name || ''}
                    sessionNumber={currentSessionNumber!}
                    sessionStartTime={state.sessionStartTime}
                    durationMinutes={getSessionDuration(currentSessionNumber!)}
                    questions={questions}
                    answers={answers}
                    currentQuestion={currentQuestion}
                    currentQuestionIndex={currentQuestionIndex}
                    tests={activeSessionTests}
                    isSubmitting={state.isSubmitting}
                    onAnswerChange={handleAnswerChange}
                    onGoToQuestion={goToQuestion}
                    onGoToNext={goToNext}
                    onGoToPrevious={goToPrevious}
                    onSubmitSession={() => handleSubmitSession()}
                    onTimeUp={() => handleSubmitSession({ force: true })}
                    onGoBack={handleGoBackWithWarning}
                />
            );

        case 'finished':
            return <SimulationCompleted />;

        default:
            return <Spinner text="Determinando estado de la simulación..." />;
    }
};

export default VirtualSimulationPresenter;
